/**
 * Componente de sección de Registros No Encontrados
 * Unmatched Records section component
 */
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { AgGridTable } from '@shared/components/ui/AgGridTable';
import { GetRowIdParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { UnmatchedRecord } from '../../models/TrackingApiResponse';

interface UnmatchedSectionProps {
  unmatchedRecords?: UnmatchedRecord[];
  total?: number;
  isLoading?: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
}

export const UnmatchedSection = ({
  unmatchedRecords = [],
  total = 0,
  isLoading = false,
  isFetching = false,
  isFetchingNextPage = false,
  fetchNextPage,
  hasNextPage = false,
}: UnmatchedSectionProps) => {
  const { t } = useTranslation('trackingModule');
  const { filteredColumnDefs } = useAgGridColumns({
    tableType: 'unmatchedRecords',
  });

  return (
    <SectionBox data-testid="unmatched-section">
      <h2 className="text-lg font-semibold text-title mb-2">{t('unmatched.title')}</h2>
      <p className="text-sm text-subtext mb-4">{t('unmatched.description')}</p>

      {isLoading && unmatchedRecords.length === 0 ? (
        <div className="text-center py-8 text-subtext">{t('loading.message')}</div>
      ) : total === 0 ? (
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.no_data.title')}
          description={t('empty.no_data.description')}
        />
      ) : (
        <div
          className={`transition-opacity duration-300 ${
            isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
          }`}
        >
          <AgGridTable
            data={unmatchedRecords}
            columnDefs={filteredColumnDefs}
            height="calc(100vh - 350px)"
            enableSelection={false}
            rowHeight={60}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            gridOptions={{
              getRowId: (params: GetRowIdParams) => `unmatched-${params.data.id}`,
            }}
          />
        </div>
      )}
    </SectionBox>
  );
};
